/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";

export const BAN_DISPUTE_REASON = {
  ATTENDED_INTERVIEW: "attended_interview",
  EMERGENCY: "emergency",
  OTHER: "other",
} as const;
export type BanDisputeReason = (typeof BAN_DISPUTE_REASON)[keyof typeof BAN_DISPUTE_REASON];

export const BAN_DISPUTE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;
export type BanDisputeStatus = (typeof BAN_DISPUTE_STATUS)[keyof typeof BAN_DISPUTE_STATUS];

export const PLACEMENT_CANDIDATE_BAN_ACTION = {
  INTERVIEW_CANCELLED: "interview:cancelled",
  INTERVIEW_RESCHEDULED: "interview:rescheduled",
  INTERVIEW_NCNS: "interview:ncns",
} as const;
export type PlacementCandidateBanAction =
  (typeof PLACEMENT_CANDIDATE_BAN_ACTION)[keyof typeof PLACEMENT_CANDIDATE_BAN_ACTION];

export const placementCandidateBanSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z
    .object({
      action: z.nativeEnum(PLACEMENT_CANDIDATE_BAN_ACTION).optional(),
      reason: z.string().min(1),
      startsAt: z.string().datetime(),
      expiresAt: z.string().datetime().optional(),
      revokedAt: z.string().datetime().optional(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
      dispute: z
        .object({
          reason: z.nativeEnum(BAN_DISPUTE_REASON),
          description: z.string(),
          status: z.nativeEnum(BAN_DISPUTE_STATUS),
        })
        .optional(),
    })
    .strict(),
  relationships: z.object({
    placementCandidate: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placementCandidate),
        id: z.string(),
      }),
    }),
    worker: z.object({
      data: z.object({
        type: z.literal(API_TYPES.worker),
        id: z.string(),
      }),
    }),
    createdBy: z.object({
      data: z.object({
        type: z.literal(API_TYPES.adminUser),
        id: z.string(),
      }),
    }),
    interview: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      })
      .optional(),
    workplace: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.workplace),
          id: z.string(),
        }),
      })
      .optional(),
  }),
  meta: z.object({
    hasDisputeEvidenceFile: z.boolean(),
  }),
});

const includedResourcesSchema = z.array(
  z.union([
    z.object({
      type: z.literal(API_TYPES.worker),
      id: z.string(),
      attributes: z.object({
        name: z.string(),
        email: z.string(),
      }),
    }),
    z.object({
      type: z.literal(API_TYPES.adminUser),
      id: z.string(),
      attributes: z.object({
        email: z.string(),
      }),
    }),
    z.object({
      type: z.literal(API_TYPES.interview),
      id: z.string(),
      attributes: z.object({
        start: z.string().datetime(),
        cancelledAt: z.string().datetime().optional(),
      }),
      relationships: z.object({
        workplace: z.object({
          data: z.object({
            type: z.literal(API_TYPES.workplace),
            id: z.string(),
          }),
        }),
      }),
    }),
    z.object({
      type: z.literal(API_TYPES.workplace),
      id: z.string(),
      attributes: z.object({
        name: z.string(),
        address: z
          .object({
            formatted: z.string().optional(),
          })
          .optional(),
      }),
    }),
  ])
);

export const placementCandidateBanGetResponseSchema = z.object({
  data: z.array(placementCandidateBanSchema),
  included: includedResourcesSchema,
  meta: z.object({
    totalCount: z.number(),
    pageSize: z.number(),
  }),
  links: z.object({
    next: z.string().optional(),
    prev: z.string().optional(),
    nextCursor: z.string().optional(),
    prevCursor: z.string().optional(),
  }),
});

export type PlacementCandidateBanResponse = z.infer<typeof placementCandidateBanSchema>;
export type PlacementCandidateBanGetResponse = z.infer<
  typeof placementCandidateBanGetResponseSchema
>;

export interface PlacementCandidateBan {
  id: string;
  action?: PlacementCandidateBanAction;
  reason: string;
  startsAt: Date;
  expiresAt?: Date;
  revokedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  placementCandidateId: string;
  workerId: string;
  workerName: string;
  workerEmail: string;
  createdById: string;
  createdByEmail: string;
  interview?: {
    id: string;
    start: Date;
    cancelledAt?: Date;
  };
  workplace?: {
    id: string;
    name: string;
    address?: {
      formatted?: string;
    };
  };
  dispute?: {
    reason: BanDisputeReason;
    description: string;
    status: BanDisputeStatus;
    hasEvidenceFile: boolean;
  };
}

export interface GetPlacementCandidateBansQuery {
  filter: {
    placementCandidateId?: string;
    workerId?: string;
    disputeStatuses?: BanDisputeStatus[];
  };
  page?: {
    size?: number;
    cursor?: string;
  };
}

export interface BansResponse {
  bans: PlacementCandidateBan[];
  nextCursor?: string;
  prevCursor?: string;
  totalCount: number;
  pageSize: number;
}

export function transformResponseIntoPlacementCandidateBans(
  response: PlacementCandidateBanGetResponse
): BansResponse {
  const workerMap = new Map(
    response.included
      .filter((resource) => resource.type === API_TYPES.worker)
      .map((worker) => [worker.id, worker.attributes])
  );

  const adminUserMap = new Map(
    response.included
      .filter((resource) => resource.type === API_TYPES.adminUser)
      .map((adminUser) => [adminUser.id, adminUser.attributes])
  );

  const interviewMap = new Map(
    response.included
      .filter((resource) => resource.type === API_TYPES.interview)
      .map((interview) => [
        interview.id,
        {
          id: interview.id,
          workplaceId: interview.relationships.workplace.data.id,
          ...interview.attributes,
        },
      ])
  );

  const workplaceMap = new Map(
    response.included
      .filter((resource) => resource.type === API_TYPES.workplace)
      .map((workplace) => [workplace.id, { id: workplace.id, ...workplace.attributes }])
  );

  const bans = response.data.map((ban) => {
    const workerId = ban.relationships.worker.data.id;
    const createdById = ban.relationships.createdBy.data.id;
    const interview = interviewMap.get(ban.relationships.interview?.data.id ?? "");
    const workplace = workplaceMap.get(interview?.workplaceId ?? "");
    return {
      id: ban.id,
      action: ban.attributes.action,
      reason: ban.attributes.reason,
      startsAt: new Date(ban.attributes.startsAt),
      expiresAt: ban.attributes.expiresAt ? new Date(ban.attributes.expiresAt) : undefined,
      revokedAt: ban.attributes.revokedAt ? new Date(ban.attributes.revokedAt) : undefined,
      createdAt: new Date(ban.attributes.createdAt),
      updatedAt: new Date(ban.attributes.updatedAt),
      placementCandidateId: ban.relationships.placementCandidate.data.id,
      workerId,
      workerName: workerMap.get(workerId)?.name ?? "Unknown",
      workerEmail: workerMap.get(workerId)?.email ?? "Unknown",
      createdById,
      createdByEmail: adminUserMap.get(createdById)?.email ?? "Unknown",
      ...(isDefined(interview) && {
        interview: {
          id: interview.id,
          start: new Date(interview.start),
          ...(isDefined(interview.cancelledAt) && { cancelledAt: new Date(interview.cancelledAt) }),
        },
      }),
      ...(isDefined(workplace) && {
        workplace: {
          id: workplace.id,
          name: workplace.name,
          address: workplace.address,
        },
      }),
      ...(isDefined(ban.attributes.dispute) && {
        dispute: { ...ban.attributes.dispute, hasEvidenceFile: ban.meta.hasDisputeEvidenceFile },
      }),
    };
  });

  return {
    bans,
    nextCursor: response.links.nextCursor,
    prevCursor: response.links.prevCursor,
    totalCount: response.meta.totalCount,
    pageSize: response.meta.pageSize,
  };
}

export const PLACEMENT_CANDIDATE_BANS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidate-bans`;

export function useFetchPaginatedPlacementCandidateBans(
  query: GetPlacementCandidateBansQuery,
  options?: UseInfiniteQueryOptions<BansResponse, Error, BansResponse>
): UseInfiniteQueryResult<BansResponse, Error> {
  return useInfiniteQuery({
    queryKey: [PLACEMENT_CANDIDATE_BANS_URL, query],
    queryFn: async ({ pageParam }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const response = await get({
        url: PLACEMENT_CANDIDATE_BANS_URL,
        queryParams: {
          filter: query.filter,
          page: {
            size: query.page?.size ?? 10,
            cursor: pageParam ?? query.page?.cursor,
          },
        },
        responseSchema: placementCandidateBanGetResponseSchema,
      });

      return transformResponseIntoPlacementCandidateBans(response.data);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    ...options,
  });
}
/* eslint-enable max-lines */
